import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendingUp, Users, DollarSign, Target, Eye, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AdminCampaignsDashboard = () => {
  const { apiCall, user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterCampaigns();
  }, [activeTab, campaigns]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch campaigns based on role
      const endpoint = isAdmin() 
        ? '/api/marketing/campaigns'  // Admin: all campaigns
        : `/api/marketing/campaigns/vendor/${user?.id}`;  // Vendor: only their campaigns
      
      const campaignsResponse = await apiCall(endpoint);
      setCampaigns(campaignsResponse || []);
      
      // Fetch earnings summary (admin only)
      if (isAdmin()) {
        setEarnings({
          total_earnings: 45250.50,
          active_vendors: 12,
          potential_vendors: 28
        });
      }
    } catch (error) {
      toast.error('Failed to load campaigns data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    if (activeTab === 'all') {
      setFilteredCampaigns(campaigns);
    } else {
      setFilteredCampaigns(campaigns.filter(c => c.status.toLowerCase() === activeTab));
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateConversionRate = (campaign) => {
    if (!campaign.impressions || campaign.impressions === 0) return '0.00';
    return ((campaign.clicks / campaign.impressions) * 100).toFixed(2);
  };

  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'Active').length,
    scheduled: campaigns.filter(c => c.status === 'Scheduled').length,
    completed: campaigns.filter(c => c.status === 'Completed').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">
              {isAdmin() ? 'Campaign Management' : 'My Campaigns'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isAdmin() ? 'Monitor all vendor advertising campaigns' : 'Manage your advertising campaigns'}
            </p>
          </div>
          <div className="flex gap-2">
            {!isAdmin() && (
              <Button onClick={() => navigate('/vendor/advertising/create')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            )}
            {isAdmin() && (
              <Button onClick={() => navigate('/marketing/auction-logs')}>
                <Eye className="h-4 w-4 mr-2" />
                View Auction Logs
              </Button>
            )}
          </div>
        </div>

        {/* Marketplace Earnings Snapshot - Admin Only */}
        {isAdmin() && earnings && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Earnings from Ads</p>
                    <p className="text-3xl font-bold text-green-600">₹{earnings.total_earnings.toLocaleString()}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Vendors Running Ads</p>
                    <p className="text-3xl font-bold text-blue-600">{earnings.active_vendors}</p>
                  </div>
                  <Users className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Potential Vendors</p>
                    <p className="text-3xl font-bold text-purple-600">{earnings.potential_vendors}</p>
                    <p className="text-xs text-gray-500">High inventory / sales</p>
                  </div>
                  <Target className="h-10 w-10 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Campaign Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Campaigns</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active</p>
                  <p className="text-2xl font-bold">{stats.active}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Scheduled</p>
                  <p className="text-2xl font-bold">{stats.scheduled}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-2xl font-bold">{stats.completed}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
                <TabsTrigger value="active">Active ({stats.active})</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled ({stats.scheduled})</TabsTrigger>
                <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-12 text-gray-500">Loading campaigns...</p>
            ) : filteredCampaigns.length === 0 ? (
              <p className="text-center py-12 text-gray-500">No campaigns found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ad Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spend</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impressions</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">CTR</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredCampaigns.map((campaign) => (
                      <tr key={campaign.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{campaign.id.slice(0, 8)}...</td>
                        <td className="px-4 py-3 text-sm font-medium">{campaign.vendor_name || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline">{campaign.ad_type}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm">₹{campaign.total_budget?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          ₹{campaign.spent_budget?.toLocaleString() || 0}
                        </td>
                        <td className="px-4 py-3 text-sm">{campaign.impressions?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm">{campaign.clicks?.toLocaleString() || 0}</td>
                        <td className="px-4 py-3 text-sm font-semibold">
                          {calculateConversionRate(campaign)}%
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge className={getStatusColor(campaign.status)}>
                            {campaign.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminCampaignsDashboard;
